import { call, put, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import api from "service/apiService";
import { ApiResponse } from "types";
import { translate } from "utils";
import { Partner, PartnerVoucher } from "../types";
import {
	createPartnerAsync,
	CreatePartnerPayload,
	getListPartnerAsync,
	GetListPartnerAsyncPayload,
	getListPartnerVoucherAsync,
	GetListPartnerVoucherAsyncPayload,
	getPartnerAsync,
	GetPartnerPayload,
	removePartnerVoucherAsync,
	RemovePartnerVoucherAsyncPayload,
	resetDetailPartner,
	setDetailPartner,
	setListPartner,
	setListPartnerVoucher,
	updatePartnerAsync,
	UpdatePartnerPayload,
	updatePartnerVoucherAsync,
	UpdatePartnerVoucherAsyncPayload,
} from "./partner.slice";

// Get list
function* getPartnersSaga(action: PayloadAction<GetListPartnerAsyncPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Partner[]>>(
				"/dashboard/partners",
				{ ...query },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setListPartner.type, payload: response });
		callback && callback();
	} else {
		message.error(translate(error.message));
	}
}

// Get list partner of voucher
function* getPartnersVoucherSaga(
	action: PayloadAction<GetListPartnerVoucherAsyncPayload>,
) {
	const { voucherId, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<PartnerVoucher[]>>(
				`/dashboard/vouchers/${voucherId}/partners`,
				query,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setListPartnerVoucher.type, payload: response });
		callback && callback();
	} else {
		message.error(translate(error.message));
	}
}

// update partners to voucher
function* updatePartnerVouchersSaga(
	action: PayloadAction<UpdatePartnerVoucherAsyncPayload>,
) {
	const { voucherId, partnerIds, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post<ApiResponse<PartnerVoucher[]>>(
				`/dashboard/vouchers/${voucherId}/partners`,
				{
					partnerIds,
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
	} else {
		message.error(translate(error.message));
	}
}

// remove partners to voucher
function* removePartnerVouchersSaga(
	action: PayloadAction<RemovePartnerVoucherAsyncPayload>,
) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.delete<
				ApiResponse<PartnerVoucher[]>
			>(`/dashboard/partners-vouchers/${id}`);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
	} else {
		message.error(translate(error.message));
	}
}

// Get detail
function* getPartnerSaga(action: PayloadAction<GetPartnerPayload>) {
	const { _id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Partner>>(
				`/dashboard/partners/${_id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback(response.data);
		yield put({ type: setDetailPartner.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

// update partner
function* updatePartnerSaga(action: PayloadAction<UpdatePartnerPayload>) {
	const { _id, callback, partner } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post<ApiResponse<Partner>>(
				`/dashboard/partners`,
				{ ...partner, _id },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback(response.data);
		yield put({ type: setDetailPartner.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

// create partner
function* createPartnerSaga(action: PayloadAction<CreatePartnerPayload>) {
	const { callback, partner } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post<ApiResponse<Partner>>(
				"/dashboard/partners",
				partner,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback(response.data);
		yield put({ type: resetDetailPartner.type });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetPartners() {
	yield takeLatest(getListPartnerAsync, getPartnersSaga);
}

function* watchGetPartnersVoucher() {
	yield takeLatest(getListPartnerVoucherAsync, getPartnersVoucherSaga);
}

function* watchUpdatePartnersVoucher() {
	yield takeLatest(updatePartnerVoucherAsync, updatePartnerVouchersSaga);
}

function* watchRemovePartnersVoucher() {
	yield takeLatest(removePartnerVoucherAsync, removePartnerVouchersSaga);
}

function* watchGetPartner() {
	yield takeLatest(getPartnerAsync, getPartnerSaga);
}

function* watchUpdatePartner() {
	yield takeLatest(updatePartnerAsync, updatePartnerSaga);
}

function* watchCreatePartner() {
	yield takeLatest(createPartnerAsync, createPartnerSaga);
}

const partnerSaga = [
	watchGetPartners(),
	watchGetPartner(),
	watchUpdatePartner(),
	watchCreatePartner(),
	watchGetPartnersVoucher(),
	watchUpdatePartnersVoucher(),
	watchRemovePartnersVoucher(),
];

export default partnerSaga;
